<script>
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { SUPPORTED_LANGUAGES } from "@/common/api/irregular-verbs.service";
import { LANGUAGE_GROUP_DANISH, LANGUAGE_GROUP_GERMAN } from "@/common/language-groups";
import { addPackageAndLangParams } from "@/common/route-path-helpers";
import FeatureCard from "@/components/FeatureCard.vue";
import ImageResponsive from "@/components/ImageResponsive.vue";
import { MatomoHelper } from "@/helpers";
import { useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default defineComponent({
  name: "WorkshopView",
  components: {
    FeatureCard,
    ImageResponsive,
    TgButton
  },
  computed: {
    ...mapStores(useSettingsStore),

    language() {
      return this.settingsStore.activeLanguageGroup;
    },
    hasIrregularVerbs() {
      return SUPPORTED_LANGUAGES.includes(this.language);
    },
    hasSpellingRules() {
      return this.language === LANGUAGE_GROUP_DANISH;
    },
    hasInflectionTrainer() {
      return this.language === LANGUAGE_GROUP_GERMAN;
    }
  },
  methods: {
    addPackageAndLangParams,

    trackIrregularVerbsClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcWorkshop,
        MatomoEventAction.ClickIrregularVerbs,
        "Clicked Irregular Verbs box"
      );
    },
    trackInflectionToolClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcWorkshop,
        MatomoEventAction.ClickInflectionTool,
        "Clicked Inflection tool box"
      );
    },
    trackSpellingRulesClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcWorkshop,
        MatomoEventAction.ClickSpellingRules,
        "Clicked Spelling rules box"
      );
    },
    trackExamInfoClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcExamPage,
        MatomoEventAction.ClickExamInfoButton,
        "Clicked Workshop info"
      );
    }
  }
});
</script>

<template>
  <main class="workshop">
    <section class="workshop__section workshop__section-start">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-7 col-lg-5 mt-md-5">
            <h1 class="workshop__headline text-md-left mb-4">
              {{ $t("workshop.start.headline") }}
            </h1>
            <p class="workshop__lead text-center text-md-left">
              {{ $t("workshop.start.lead") }}
            </p>
          </div>
          <div class="col-12 col-md-5 col-lg-7 align-self-center mt-5 mt-md-0">
            <ImageResponsive src="/assets/img/workshop/boy-girl-tools@1x.png" presentational />
          </div>
        </div>
      </div>
    </section>
    <section class="workshop__section">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-4 mb-5 mb-md-0">
            <FeatureCard
              image-src="/assets/img/workshop/irregular-verbs-illustration@1x.png"
              :title="$t('workshop.features.headlines.irregularVerbs')"
              :button-label="$t('workshop.features.buttonTexts.irregularVerbs')"
              :button-disabled="!hasIrregularVerbs"
              :link-to="addPackageAndLangParams({ name: 'workshop.irregular-verbs' })"
              @button-click="trackIrregularVerbsClick()"
            />
          </div>
          <div class="col-12 col-md-4 mb-5 mb-md-0">
            <FeatureCard
              image-src="/assets/img/workshop/inflection-scheme-illustration@1x.png"
              :title="$t('workshop.features.headlines.inflectionTool')"
              :button-label="
                hasInflectionTrainer
                  ? $t('workshop.features.buttonTexts.inflectionTrainer')
                  : $t('workshop.features.buttonTexts.inflectionTrainerNotAvailable')
              "
              :button-disabled="!hasInflectionTrainer"
              :link-to="addPackageAndLangParams({ name: 'workshop.inflection-trainer' })"
              @button-click="trackInflectionToolClick()"
            />
          </div>
          <div class="col-12 col-md-4 mb-5 mb-md-0">
            <FeatureCard
              image-src="/assets/img/workshop/checkbox-illustration@1x.png"
              :title="$t('workshop.features.headlines.orthographicRules')"
              :button-label="
                hasSpellingRules
                  ? $t('workshop.features.buttonTexts.orthographicRules')
                  : $t('workshop.features.buttonTexts.orthographicRulesNotAvailable')
              "
              :button-disabled="!hasSpellingRules"
              :link-to="addPackageAndLangParams({ name: 'workshop.dsn-spelling-rules' })"
              @button-click="trackSpellingRulesClick()"
            />
          </div>
        </div>
      </div>
    </section>

    <section v-if="false" class="workshop__section">
      <div class="container">
        <div class="row">
          <div class="col-12 col-md-7 d-flex flex-column justify-content-center">
            <h2 class="workshop__headline workshop__headline--sm text-md-left mb-3 mb-md-5">
              {{ $t("workshop.examPreparation.headline") }}
            </h2>
            <p class="workshop__lead">{{ $t("workshop.examPreparation.lead") }}</p>
            <div class="text-center text-md-left mt-4">
              <TgButton
                tag="router-link"
                :to="{ name: 'exam-preparation' }"
                @click="trackExamInfoClick()"
              >
                {{ $t("workshop.examPreparation.button") }}
              </TgButton>
            </div>
          </div>
          <div class="d-none d-md-block col-5 mt-md-3">
            <ImageResponsive
              src="/assets/img/exam-preparation/lofi-abc-girl@1x.png"
              presentational
            />
          </div>
        </div>
      </div>
    </section>
  </main>
</template>
